import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { forwardRef, useMemo, useState } from 'react';
import { TextInput } from '@paypalcorp/pp-react-text-input';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { DeclineIcon, SearchIcon } from '@paypalcorp/pp-react-icons';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { math } from 'polished';
import { devWarning } from '@paypalcorp/pp-react-utils';
var _templateObject, _templateObject2, _templateObject3, _templateObject4;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
    sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
    sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
    sysColorErrorMain = tokens.sysColorErrorMain,
    sysSizeIconSm = tokens.sysSizeIconSm,
    sysSizeIconXl = tokens.sysSizeIconXl,
    sysSpaceMd = tokens.sysSpaceMd,
    sysSpaceLg = tokens.sysSpaceLg;
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: search_input;\n    border-radius: ", ";\n    padding-left: ", ";\n\n    &:not(:focus) {\n      border-color: ", ";\n      box-shadow: none;\n    }\n\n    [dir='rtl'] & {\n      padding-right: ", ";\n    }\n  "])), sysSizeIconXl, math("3 * ".concat(sysSpaceMd, " + ").concat(sysSizeIconSm, " - ").concat(sysBorderThicknessNormal)), sysColorBackgroundMediumContrast, math("3 * ".concat(sysSpaceMd, " + ").concat(sysSizeIconSm, " - ").concat(sysBorderThicknessNormal)));
  var label = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: label;\n    left: ", ";\n\n    [dir='rtl'] & {\n      right: ", ";\n    }\n  "])), math("3 * ".concat(sysSpaceMd, " + ").concat(sysSizeIconSm)), math("3 * ".concat(sysSpaceMd, " + ").concat(sysSizeIconSm)));
  var leftIcon = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: left_icon;\n    left: ", ";\n\n    [dir='rtl'] & {\n      right: ", ";\n    }\n  "])), math("2 * ".concat(sysSpaceMd)), math("2 * ".concat(sysSpaceMd)));
  var error = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: error;\n    padding: ", "\n      ", "\n      ", "\n      ", ";\n\n    &:not(:focus) {\n      border-color: ", ";\n      box-shadow: none;\n    }\n  "])), math("".concat(sysSpaceLg, " + ").concat(sysBorderThicknessEmphasis)), math("3 * ".concat(sysSpaceMd, " + ").concat(sysSizeIconSm, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceLg, " + ").concat(sysBorderThicknessEmphasis)), math("3 * ".concat(sysSpaceMd, " + ").concat(sysSizeIconSm, " - ").concat(sysBorderThicknessEmphasis)), sysColorErrorMain);
  return {
    base: base,
    label: label,
    leftIcon: leftIcon,
    error: error
  };
}
var _excluded = ["keepClearButton", "onClear", "className", "clearButtonLabel", "clearButtonAriaLabel", "inputClassName", "labelClassName", "errorText", "label", "value", "onKeyDown"];
var SearchInput = forwardRef(function (props, ref) {
  var keepClearButton = props.keepClearButton,
    onClear = props.onClear,
    className = props.className,
    _props$clearButtonLab = props.clearButtonLabel,
    clearButtonLabel = _props$clearButtonLab === void 0 ? 'Clear input' : _props$clearButtonLab,
    _props$clearButtonAri = props.clearButtonAriaLabel,
    clearButtonAriaLabel = _props$clearButtonAri === void 0 ? 'The info has been cleared' : _props$clearButtonAri,
    inputClassName = props.inputClassName,
    labelClassName = props.labelClassName,
    errorText = props.errorText,
    label = props.label,
    value = props.value,
    onKeyDown = props.onKeyDown,
    rest = _objectWithoutProperties(props, _excluded);
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    cleared = _useState2[0],
    setCleared = _useState2[1];

  /* istanbul ignore next */
  var handleKey = function (evt) {
    if (cleared) {
      setCleared(false);
    }
    if (evt.key === 'Escape' && onClear) {
      onClear(evt);
    }
    if (onKeyDown) {
      onKeyDown(evt);
    }
  };
  var clearButtonProps = cleared ? {
    'aria-label': clearButtonAriaLabel,
    'aria-atomic': 'true',
    'aria-live': 'polite',
    role: 'button'
  } : null;
  var clearButton = value || keepClearButton ? {
    rightIcon: jsx(IconButton, _extends({
      buttonType: "tertiary",
      svgIconComponent: DeclineIcon,
      onClick: function handleClearClick(evt) {
        /* istanbul ignore next */
        if (onClear) {
          onClear(evt);
          setCleared(true);
        }
      },
      onMouseDown: function onMouseDown(evt) {
        return evt.preventDefault();
      } // remove focus on click
    }, clearButtonProps, {
      "data-ppui": "true"
    }), clearButtonLabel)
  } : null;
  return jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref) {
    var css = _ref.css,
      cx = _ref.cx;
    return jsx(TextInput, _extends({
      className: className,
      ref: ref,
      leftIcon: jsx(SearchIcon, {
        size: "sm",
        "aria-hidden": "true",
        "data-ppui": "true"
      }),
      leftIconCSS: _styles.leftIcon
    }, clearButton, {
      inputClassName: cx(css([_styles.base, errorText && _styles.error]), inputClassName),
      labelClassName: cx(css([_styles.label]), labelClassName),
      errorText: errorText,
      isLabelHidden: true,
      placeholder: label,
      label: label,
      value: value,
      "data-ppui-info": "search-input_2.3.7"
    }, rest, {
      onKeyDown: handleKey
    }));
  });
});
export { SearchInput, styles as searchInputStyles };